<script setup lang="ts">
  import { computed } from 'vue';
  import { BaseDropdown } from '@/shared/ui/base-dropdown';
  import { ResponsiveOverlayCard } from '@/shared/ui/responsive-overlay-card';
  import { solApi } from '@/shared/api';
  import { useShareDialog } from '@/shared/model';
  import { fetchMedia } from '@/shared/lib';
  import { createMagicEdenNftUrl, parseNftName } from '@/entities/token';
  import { useMagicEdenNft } from '../model/composables/use-magic-eden-nft';

  const props = defineProps<{
    item: solApi.collection.MagicEdenAuctionData;
    purchased?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'buy'): void;
  }>();

  const { repost } = useMagicEdenNft();
  const { openShareDialog } = useShareDialog();

  const rarity = computed(() => {
    return props.item?.rarity?.moonrank?.absolute_rarity;
  });

  const buyOnME = computed(
    () =>
      !props.item?.seller ||
      !props.item?.auctionHouse ||
      !props.item?.token.mintAddress ||
      !props.item?.tokenAddress ||
      !props.item?.price,
  );

  const share = () => {
    openShareDialog(createMagicEdenNftUrl(props.item.token.mintAddress));
  };
</script>

<template>
  <responsive-overlay-card class="h-98">
    <div class="size-full">
      <img
        v-lazy="fetchMedia(item.token.image)"
        class="size-full object-cover !rounded-none"
      />
    </div>
    <template #footer>
      <footer class="flex justify-between w-full p-6">
        <div class="flex gap-2 items-center">
          <span class="font-medium">
            {{ parseNftName(item.token.name).numberOrName }}
          </span>
          <span v-if="rarity" class="text-gray-400 text-sm">{{ rarity }}</span>
        </div>

        <div class="flex gap-2 items-center">
          <base-icon name="solana" size="size-5" />
          <span>{{ item.price }}</span>
        </div>
      </footer>
    </template>
    <template #overlay>
      <div class="flex justify-end items-center w-full">
        <base-button
          v-if="purchased"
          size="small"
          rounded="rounded-full"
          variant="glass"
          class="text-green-500"
          disabled
        >
          {{ $t('purchased') }}
        </base-button>
        <base-button
          v-else
          size="small"
          rounded="rounded-full drop-shadow-heavy"
          target="_blank"
          :variant="item?.price ? 'glass' : 'destructive'"
          :disabled="!item?.price"
          :to="
            buyOnME ? createMagicEdenNftUrl(item.token.mintAddress) : undefined
          "
          @click="buyOnME ? undefined : $emit('buy')"
        >
          <base-icon
            v-if="item?.price"
            name="vuesax-linear-shopping-cart"
            size="size-5"
          />
          <span>{{
            item?.price ? (buyOnME ? 'Buy on ME' : 'Buy') : 'Sold'
          }}</span>
        </base-button>
        <base-dropdown
          placement="left-start"
          custom-content-classes="border border-gray-785 border-opacity-50 bg-gray-725 rounded-2xl overflow-hidden drop-shadow-light"
          :distance="-5"
        >
          <template #button="{ toggle }">
            <base-button
              variant="custom"
              size="small"
              additional-classes="drop-shadow-heavy"
              @click="toggle"
            >
              <base-icon
                name="vertical-dots"
                size="size-5"
                class="text-white"
              />
            </base-button>
          </template>
          <template #content="{ hide }">
            <ul class="flex flex-col">
              <li
                class="flex cursor-pointer hover:bg-gradient-to-r hover:from-indigo-850 hover:via-transparent hover:to-transparent transition-all"
                @click="hide()"
              >
                <base-button
                  variant="custom"
                  custom-classes="px-5 py-4 w-full text-left"
                  target="_blank"
                  :to="createMagicEdenNftUrl(item.token.mintAddress)"
                >
                  <span class="whitespace-nowrap">
                    {{ $t('viewOnMagicEden') }}
                  </span>
                </base-button>
              </li>
              <li
                class="flex px-5 py-4 cursor-pointer hover:bg-gradient-to-r hover:from-indigo-850 hover:via-transparent hover:to-transparent transition-all"
                @click="hide(), share()"
              >
                <span class="whitespace-nowrap capitalize">
                  {{ $t('share') }}
                </span>
              </li>
              <li
                class="flex px-5 py-4 cursor-pointer hover:bg-gradient-to-r hover:from-indigo-850 hover:via-transparent hover:to-transparent transition-all"
                @click="hide(), repost(item.token.mintAddress)"
              >
                <span class="whitespace-nowrap capitalize">
                  {{ $t('post') }}
                </span>
              </li>
            </ul>
          </template>
        </base-dropdown>
      </div>
      <base-button
        variant="glass"
        size="small"
        rounded="rounded-full"
        additional-classes="ml-3.5 drop-shadow-heavy"
        target="_blank"
        :to="createMagicEdenNftUrl(item.token.mintAddress)"
      >
        <base-icon name="branded-magic-eden" size="size-5" />
        <span>magiceden.io</span>
      </base-button>
    </template>
  </responsive-overlay-card>
</template>
